import React from "react";
import {Button, Empty, Popconfirm, Space, Table, Tag, Tooltip} from "antd";
import {Download, FileText, Trash2} from "lucide-react";
import {useTranslation} from "react-i18next";

const extensionLabel = (extension) => String(extension || "file").replace(".", "").toUpperCase();

const StatusTag = ({active, t}) => (
    <Tag className={`ref-status-tag ${active ? "is-active" : "is-inactive"}`}>
        {active ? t("status.active") : t("status.inactive")}
    </Tag>
);

const DocumentActions = ({record, onDownload, onDelete, t}) => (
    <Space className="document-actions" size={4}>
        <Tooltip title={t("actions.download")}>
            <Button
                aria-label={t("actions.download")}
                icon={<Download size={16}/>}
                onClick={() => onDownload(record)}
                type="text"
            />
        </Tooltip>

        <Popconfirm
            title={t("actions.confirmDelete")}
            description={t("actions.deleteDescription")}
            onConfirm={() => onDelete(record.id)}
            okText={t("actions.confirm")}
            cancelText={t("actions.cancel")}
        >
            <Tooltip title={t("actions.delete")}>
                <Button
                    aria-label={t("actions.delete")}
                    icon={<Trash2 size={16}/>}
                    type="text"
                    danger
                />
            </Tooltip>
        </Popconfirm>
    </Space>
);

const columns = (t, onDownload, onDelete, showStatus) => [
    {
        title: t("table.title"),
        dataIndex: "title",
        key: "title",
        render: (title, record) => (
            <div className="document-title-cell">
                <span className="document-file-icon">
                    <FileText size={17}/>
                </span>
                <div>
                    <strong>{title}</strong>
                    <span>{extensionLabel(record.file_extension)}</span>
                </div>
            </div>
        ),
    },
    showStatus && {
        title: t("table.status"),
        dataIndex: "is_active",
        key: "is_active",
        width: 120,
        render: (active) => <StatusTag active={active} t={t}/>,
    },
    {
        title: t("table.createdAt"),
        dataIndex: "created_at",
        key: "created_at",
        width: 170,
        render: (createdAt) => <span className="document-date">{createdAt || "-"}</span>,
    },
    {
        title: t("table.actions"),
        key: "actions",
        width: 110,
        align: "right",
        render: (_, record) => (
            <DocumentActions
                record={record}
                onDownload={onDownload}
                onDelete={onDelete}
                t={t}
            />
        ),
    },
].filter(Boolean);

function DocumentTable({
    documents,
    onDownload,
    onDelete,
}) {
    const {t} = useTranslation("referentiel");
    const showStatus = documents.some((document) => !document.is_active);

    return (
        <div className="document-library">
            <Table
                className="document-table"
                rowKey="id"
                size="middle"
                dataSource={documents}
                columns={columns(t, onDownload, onDelete, showStatus)}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t("table.empty")}
                        />
                    ),
                }}
                pagination={{
                    pageSize: 8,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                }}
                scroll={{x: showStatus ? 680 : 560}}
            />

            <div className="document-mobile-list">
                {documents.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table.empty")}/>
                ) : (
                    documents.map((record) => (
                        <article className="document-mobile-card" key={record.id}>
                            <div className="document-mobile-main">
                                <span className="document-file-icon">
                                    <FileText size={17}/>
                                </span>
                                <div>
                                    <strong>{record.title}</strong>
                                    <span>{extensionLabel(record.file_extension)} - {record.created_at || "-"}</span>
                                </div>
                            </div>

                            <div className="document-mobile-footer">
                                {!record.is_active && <StatusTag active={false} t={t}/>}
                                <DocumentActions
                                    record={record}
                                    onDownload={onDownload}
                                    onDelete={onDelete}
                                    t={t}
                                />
                            </div>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
}

export default DocumentTable;
